/**
 * Integrytas Group — Record Extensions: edit-modal section.
 *
 * Renders every registered extension's manifest fields (and its address children)
 * inside a host page's add/edit modal. The host does not know what the fields
 * ARE — it just drops this in and awaits `form.save(...)` after its own save.
 *
 * Because the platform renders the manifest, the §17 conventions come for free:
 * each field is wrapped in <PersonalizedField> so it can be hidden / made
 * read-only / made required per user and role (§17.A); lookup and FK values are
 * chosen by resolved NAME through the filtered modal select, never by raw code
 * (§17.B4, §11); Y/N is a real control, not a typed character (§17.B10); and
 * addresses use <AddressEditor>, never a free-text line (§17.B8).
 */

import { useTranslation } from 'react-i18next';
import { AddressEditor } from './AddressEditor';
import { FilteredModalSingleSelect } from './FilteredModalSingleSelect';
import { PersonalizedField } from '../personalization';
import type { ExtensionField, RecordExtension } from '../../lib/record_extensions';
import type { UseRecordExtensionForm } from '../../hooks/use_record_extension_form';

const inputCls = 'mb-3 h-8 w-full rounded border border-csm-border px-2 text-csm-md focus:border-primary focus:outline-none';
const labelCls = 'mb-1 block text-csm-sm font-medium text-csm-ink-soft';

interface Props {
  form: UseRecordExtensionForm;
  /** Personalization component key of the host modal (e.g. 'drawer'). */
  componentKey: string;
}

function ScalarInput({ field, value, disabled, onChange }: {
  field: ExtensionField;
  value: unknown;
  disabled: boolean;
  onChange: (v: unknown) => void;
}) {
  const { t } = useTranslation();
  if (field.kind === 'yn') {
    return (
      <label className="mb-3 flex items-center gap-2 text-csm-sm text-csm-ink-soft">
        <input type="checkbox" className="h-4 w-4 accent-gray-700" disabled={disabled}
          checked={value === 'Y'} onChange={e => onChange(e.target.checked ? 'Y' : 'N')} />
        {t(field.label_key, field.label_default)}
      </label>
    );
  }
  const type = field.kind === 'number' ? 'number' : field.kind === 'date' ? 'date' : 'text';
  return (
    <input
      type={type}
      className={inputCls}
      disabled={disabled}
      value={value == null ? '' : String(value)}
      onChange={e => onChange(e.target.value)}
    />
  );
}

function ExtensionFieldControl({ ext, field, form, componentKey }: {
  ext: RecordExtension;
  field: ExtensionField;
  form: UseRecordExtensionForm;
  componentKey: string;
}) {
  const { t } = useTranslation();
  const label = t(field.label_key, field.label_default);
  const value = form.values[ext.key]?.[field.key] ?? '';
  // A business reference is assigned once; after creation it is displayed, not re-keyed.
  const locked = Boolean(field.immutable_after_create) && !form.isNew(ext.key);
  const set = (v: unknown) => form.setValue(ext.key, field.key, v);

  if (field.kind === 'lookup' || field.kind === 'fk') {
    const map = form.options[ext.key]?.[field.key] ?? {};
    const options = Object.entries(map)
      .map(([code, display_text]) => ({ internal_code: code, display_text }))
      .sort((a, b) => a.display_text.localeCompare(b.display_text));
    return (
      <PersonalizedField componentKey={componentKey} fieldKey={field.key} label={label}>
        <FilteredModalSingleSelect
          label={label}
          options={options}
          selectedCode={value === '' || value == null ? null : String(value)}
          onChange={code => set(code)}
          allowClear={!field.required}
        />
        <div className="mb-3" />
      </PersonalizedField>
    );
  }

  return (
    <PersonalizedField componentKey={componentKey} fieldKey={field.key} label={label}>
      {field.kind !== 'yn' && <label className={labelCls}>{label}</label>}
      <ScalarInput field={field} value={value} disabled={locked} onChange={set} />
    </PersonalizedField>
  );
}

export function RecordExtensionFields({ form, componentKey }: Props) {
  const { t } = useTranslation();
  if (form.extensions.length === 0) return null;

  return (
    <>
      {form.extensions.map(ext => {
        const addressChildren = (ext.children ?? []).filter(c => c.kind === 'address');
        return (
          <div key={ext.key} className="mb-4 rounded border border-csm-border px-3 py-3">
            {ext.section_label_key && (
              <div className="mb-3 text-csm-sm font-medium text-csm-ink">
                {t(ext.section_label_key, ext.section_label_default ?? ext.key)}
              </div>
            )}
            {ext.fields.map(field => (
              <ExtensionFieldControl key={field.key} ext={ext} field={field} form={form} componentKey={componentKey} />
            ))}
            {addressChildren.map(child => child.kind === 'address' && (
              <AddressEditor
                key={child.key}
                value={form.addresses[`${ext.key}:${child.key}`] ?? []}
                onChange={rows => form.setAddresses(ext.key, child.key, rows)}
                types={child.types}
                label={t(child.label_key, child.label_default)}
              />
            ))}
          </div>
        );
      })}
    </>
  );
}
